import { Circle, Fill, Stroke, Style, Icon, Text, RegularShape } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';
//import type { Geometry } from 'ol/geom';
import { getConfigOptional } from './config';

// ============================================================================
// Типы конфигурации стилей
// ============================================================================

export interface BaseStyleConfig {
  type: 'circle' | 'line' | 'polygon' | 'icon' | 'square' | 'triangle' | 'text';
}

export interface CircleStyleConfig extends BaseStyleConfig {
  type: 'circle';
  radius?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface LineStyleConfig extends BaseStyleConfig {
  type: 'line';
  stroke?: string;
  strokeWidth?: number;
  lineDash?: number[];
}

export interface PolygonStyleConfig extends BaseStyleConfig {
  type: 'polygon';
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  lineDash?: number[];
}

export interface IconStyleConfig extends BaseStyleConfig {
  type: 'icon';
  src: string;
  scale?: number;
  anchor?: [number, number];
  rotation?: number;
}

export interface SquareStyleConfig extends BaseStyleConfig {
  type: 'square';
  size?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

export interface TriangleStyleConfig extends BaseStyleConfig {
  type: 'triangle';
  radius?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  rotation?: number;
}

export interface TextStyleConfig extends BaseStyleConfig {
  type: 'text';
  text?: string;
  field?: string; // Имя поля из атрибутов фичи
  font?: string;
  size?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  offsetX?: number;
  offsetY?: number;
}

// ============================================================================
// Типы для условной раскраски (хороплет)
// ============================================================================

export interface ChoroplethClass {
  name: string;       // Название класса (для легенды)
  minValue: number;   // Минимальное значение (включительно)
  color: string;      // Цвет заполнения/обводки
}

export interface ChoroplethConfig {
  attribute: string;              // Имя атрибута фичи
  classes: ChoroplethClass[];     // Список классов
  defaultColor?: string;          // Цвет для значений вне классов или null
  applyTo?: 'fill' | 'stroke';    // Куда применять цвет (по умолчанию fill)
}

// ============================================================================
// Типы для градиентной раскраски
// ============================================================================

export interface GradientStop {
  value: number;  // Значение
  color: string;  // Цвет (hex, rgb, rgba)
}

export interface GradientConfig {
  attribute: string;              // Имя атрибута фичи
  stops: GradientStop[];          // Опорные точки градиента
  defaultColor?: string;          // Цвет для значений вне диапазона или null
  applyTo?: 'fill' | 'stroke';    // Куда применять цвет (по умолчанию fill)
}

// Расширяем базовый тип стиля
export interface BaseStyleConfig {
  type: 'circle' | 'line' | 'polygon' | 'icon' | 'square' | 'triangle' | 'text';
  choropleth?: ChoroplethConfig;
  gradient?: GradientConfig;  // <-- НОВОЕ
}

export type StyleConfig =
  | CircleStyleConfig
  | LineStyleConfig
  | PolygonStyleConfig
  | IconStyleConfig
  | SquareStyleConfig
  | TriangleStyleConfig
  | TextStyleConfig;

// ============================================================================
// Вспомогательные функции
// ============================================================================

function createFill(color?: string): Fill | undefined {
  if (!color || color === 'none' || color === 'transparent' || color === '') {
    return undefined;
  }
  return new Fill({ color });
}

function createStroke(color?: string, width?: number, lineDash?: number[]): Stroke | undefined {
  if (!color) return undefined;
  return new Stroke({
    color,
    width: width || 1,
    lineDash: lineDash
  });
}

// ============================================================================
// Типы для условной раскраски (хороплет)
// ============================================================================

export interface ChoroplethClass {
  name: string;       // Название класса (для легенды)
  minValue: number;   // Минимальное значение (включительно)
  color: string;      // Цвет заполнения/обводки
}

export interface ChoroplethConfig {
  attribute: string;              // Имя атрибута фичи
  classes: ChoroplethClass[];     // Список классов
  defaultColor?: string;          // Цвет для значений вне классов или null
  applyTo?: 'fill' | 'stroke';    // Куда применять цвет (по умолчанию fill)
}

// Расширяем базовый тип стиля
export interface BaseStyleConfig {
  type: 'circle' | 'line' | 'polygon' | 'icon' | 'square' | 'triangle' | 'text';
  choropleth?: ChoroplethConfig;  // <-- НОВОЕ
}

// ============================================================================
// Классификация значений
// ============================================================================

/**
 * Нормализует список классов: сортирует по minValue и вычисляет границы.
 * Возвращает массив интервалов: [{ min, max, color, name }]
 * Для последнего класса max = Infinity.
 */
function normalizeChoroplethClasses(classes: ChoroplethClass[]): Array<{
  min: number;
  max: number;
  color: string;
  name: string;
}> {
  // Сортируем по возрастанию minValue
  const sorted = [...classes].sort((a, b) => a.minValue - b.minValue);
  
  return sorted.map((cls, index) => {
    const nextMin = index < sorted.length - 1 ? sorted[index + 1].minValue : Infinity;
    return {
      min: cls.minValue,
      max: nextMin,
      color: cls.color,
      name: cls.name
    };
  });
}

/**
 * Находит цвет для заданного значения по классам.
 * Возвращает null, если значение не попадает ни в один класс.
 */
function findColorForValue(
  value: number | null | undefined,
  normalizedClasses: ReturnType<typeof normalizeChoroplethClasses>,
  defaultColor?: string
): string | null {
  if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
    return defaultColor || null;
  }
  
  for (const cls of normalizedClasses) {
    // minValue <= value < nextMinValue
    if (value >= cls.min && value < cls.max) {
      return cls.color;
    }
  }
  
  // Значение ниже первого класса или выше последнего (невозможно, т.к. последний до Infinity)
  return defaultColor || null;
}

/**
 * Создаёт функцию стиля с условной раскраской.
 */
function createChoroplethStyleFunction(
  baseStyleConfig: StyleConfig,
  choropleth: ChoroplethConfig
): (feature: FeatureLike, _resolution: number) => Style {
  const normalizedClasses = normalizeChoroplethClasses(choropleth.classes);
  const applyTo = choropleth.applyTo || 'fill';
  
  // Кэш стилей по цвету (чтобы не создавать новые Style для каждой фичи)
  const styleCache = new Map<string, Style>();
  
  function getStyleForColor(color: string): Style {
    if (styleCache.has(color)) return styleCache.get(color)!;
    
    // Создаём копию базового стиля с изменённым цветом
    const style = createStyle({ ...baseStyleConfig });
    
    // Применяем цвет в зависимости от типа стиля и настройки applyTo
    const type = baseStyleConfig.type;
    
    if (type === 'circle' || type === 'square' || type === 'triangle') {
      // Для точечных стилей меняем fill изображения
      const image = style.getImage();
      if (image && 'setFill' in image) {
        // Клонируем изображение с новым fill
        const clonedImage = image.clone();
        if (applyTo === 'fill' && 'setFill' in clonedImage) {
          (clonedImage as any).setFill(new Fill({ color }));
          style.setImage(clonedImage);
        } else if (applyTo === 'stroke' && 'setStroke' in clonedImage) {
          const existingStroke = (clonedImage as any).getStroke();
          if (existingStroke) {
            (clonedImage as any).setStroke(new Stroke({
              color,
              width: existingStroke.getWidth()
            }));
          }
          style.setImage(clonedImage);
        }
      }
    } else if (type === 'polygon') {
      if (applyTo === 'fill') {
        style.setFill(new Fill({ color }));
      } else if (applyTo === 'stroke') {
        const existingStroke = style.getStroke();
        style.setStroke(new Stroke({
          color,
          width: existingStroke?.getWidth() || 2
        }));
      }
    } else if (type === 'line') {
      // Для линий цвет всегда применяется к stroke
      const existingStroke = style.getStroke();
      style.setStroke(new Stroke({
        color,
        width: existingStroke?.getWidth() || 3,
        lineDash: existingStroke?.getLineDash() || undefined
      }));
    }
    
    styleCache.set(color, style);
    return style;
  }
  
  // Стиль для значений вне классов
  const defaultStyle = choropleth.defaultColor 
    ? getStyleForColor(choropleth.defaultColor) 
    : createStyle(baseStyleConfig);
  
  return (feature: FeatureLike, _resolution: number): Style => {
    const rawValue = feature.get(choropleth.attribute);
    const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    
    const color = findColorForValue(value, normalizedClasses, choropleth.defaultColor);
    if (!color) return defaultStyle;
    
    return getStyleForColor(color);
  };
}

/**
 * Возвращает нормализованные классы для построения легенды.
 */
export function getChoroplethLegend(layerId: string): {
  classes: Array<{ name: string; min: number; max: number; color: string }>;
  noData?: { color: string };  // <-- НОВОЕ
} | null {
  const styles = getConfigOptional('layer-styles');
  if (!styles) return null;
  
  const layerStyle = styles[layerId] as any;
  if (!layerStyle?.choropleth) return null;
  
  const result: any = {
    classes: normalizeChoroplethClasses(layerStyle.choropleth.classes)
  };
  
  // Если задан defaultColor — добавляем информацию о "Нет данных"
  if (layerStyle.choropleth.defaultColor) {
    result.noData = { color: layerStyle.choropleth.defaultColor };
  }
  
  return result;
}

// ============================================================================
// Интерполяция цветов
// ============================================================================

/**
 * Парсит цвет из строки в RGB-компоненты.
 * Поддерживает hex (#RGB, #RRGGBB, #RRGGBBAA), rgb(), rgba()
 */
function parseColor(color: string): { r: number; g: number; b: number; a: number } {
  color = color.trim();
  
  // Hex формат
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length === 6) {
      hex += 'ff';
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = parseInt(hex.slice(6, 8), 16) / 255;
    return { r, g, b, a };
  }
  
  // RGB/RGBA формат
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]),
      g: parseInt(rgbMatch[2]),
      b: parseInt(rgbMatch[3]),
      a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1
    };
  }
  
  // Fallback: чёрный
  return { r: 0, g: 0, b: 0, a: 1 };
}

/**
 * Преобразует RGB-компоненты в строку цвета.
 */
function colorToString(color: { r: number; g: number; b: number; a: number }): string {
  if (color.a === 1) {
    return `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;
  }
  return `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)}, ${color.a.toFixed(2)})`;
}

/**
 * Линейно интерполирует между двумя цветами.
 */
function interpolateColor(
  color1: { r: number; g: number; b: number; a: number },
  color2: { r: number; g: number; b: number; a: number },
  t: number
): { r: number; g: number; b: number; a: number } {
  return {
    r: color1.r + (color2.r - color1.r) * t,
    g: color1.g + (color2.g - color1.g) * t,
    b: color1.b + (color2.b - color1.b) * t,
    a: color1.a + (color2.a - color1.a) * t
  };
}

/**
 * Нормализует stops: сортирует по value и вычисляет min/max.
 */
function normalizeGradientStops(stops: GradientStop[]): {
  sorted: Array<{ value: number; color: { r: number; g: number; b: number; a: number } }>;
  min: number;
  max: number;
} {
  const sorted = [...stops]
    .sort((a, b) => a.value - b.value)
    .map(stop => ({
      value: stop.value,
      color: parseColor(stop.color)
    }));
  
  const min = sorted[0]?.value ?? 0;
  const max = sorted[sorted.length - 1]?.value ?? 0;
  
  return { sorted, min, max };
}

/**
 * Вычисляет цвет для заданного значения по градиенту.
 */
function getColorForValue(
  value: number,
  normalizedStops: ReturnType<typeof normalizeGradientStops>,
  defaultColor?: string
): string | null {
  const { sorted, min, max } = normalizedStops;
  
  if (sorted.length === 0) return defaultColor || null;
  if (sorted.length === 1) return colorToString(sorted[0].color);
  
  // Значение ниже минимума
  if (value < min) return defaultColor || null;
  
  // Значение выше максимума
  if (value > max) return defaultColor || null;
  
  // Находим два ближайших stops
  for (let i = 0; i < sorted.length - 1; i++) {
    const stop1 = sorted[i];
    const stop2 = sorted[i + 1];
    
    if (value >= stop1.value && value <= stop2.value) {
      // Линейная интерполяция
      const t = (value - stop1.value) / (stop2.value - stop1.value);
      const interpolated = interpolateColor(stop1.color, stop2.color, t);
      return colorToString(interpolated);
    }
  }
  
  return defaultColor || null;
}

/**
 * Создаёт функцию стиля с градиентной раскраской.
 */
function createGradientStyleFunction(
  baseStyleConfig: StyleConfig,
  gradient: GradientConfig
): (feature: FeatureLike, _resolution: number) => Style {
  const normalizedStops = normalizeGradientStops(gradient.stops);
  const applyTo = gradient.applyTo || 'fill';
  
  // Кэш стилей по цвету
  const styleCache = new Map<string, Style>();
  
  function getStyleForColor(color: string): Style {
    if (styleCache.has(color)) return styleCache.get(color)!;
    
    const style = createStyle({ ...baseStyleConfig });
    const type = baseStyleConfig.type;
    
    if (type === 'circle' || type === 'square' || type === 'triangle') {
      const image = style.getImage();
      if (image && 'setFill' in image) {
        const clonedImage = image.clone();
        if (applyTo === 'fill' && 'setFill' in clonedImage) {
          (clonedImage as any).setFill(new Fill({ color }));
          style.setImage(clonedImage);
        } else if (applyTo === 'stroke' && 'setStroke' in clonedImage) {
          const existingStroke = (clonedImage as any).getStroke();
          if (existingStroke) {
            (clonedImage as any).setStroke(new Stroke({
              color,
              width: existingStroke.getWidth()
            }));
          }
          style.setImage(clonedImage);
        }
      }
    } else if (type === 'polygon') {
      if (applyTo === 'fill') {
        style.setFill(new Fill({ color }));
      } else if (applyTo === 'stroke') {
        const existingStroke = style.getStroke();
        style.setStroke(new Stroke({
          color,
          width: existingStroke?.getWidth() || 2
        }));
      }
    } else if (type === 'line') {
      const existingStroke = style.getStroke();
      style.setStroke(new Stroke({
        color,
        width: existingStroke?.getWidth() || 3,
        lineDash: existingStroke?.getLineDash() || undefined
      }));
    }
    
    styleCache.set(color, style);
    return style;
  }
  
  const defaultStyle = gradient.defaultColor 
    ? getStyleForColor(gradient.defaultColor) 
    : createStyle(baseStyleConfig);
  
  return (feature: FeatureLike, _resolution: number): Style => {
    const rawValue = feature.get(gradient.attribute);
    const value = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
    
    if (typeof value !== 'number' || isNaN(value)) {
      return defaultStyle;
    }
    
    const color = getColorForValue(value, normalizedStops, gradient.defaultColor);
    if (!color) return defaultStyle;
    
    return getStyleForColor(color);
  };
}

/**
 * Возвращает информацию о градиенте для построения легенды.
 */
export function getGradientLegend(layerId: string): {
  stops: Array<{ value: number; color: string }>;
  min: number;
  max: number;
  noData?: { color: string };
} | null {
  const styles = getConfigOptional('layer-styles');
  if (!styles) return null;
  
  const layerStyle = styles[layerId] as any;
  if (!layerStyle?.gradient) return null;
  
  const sortedStops = [...layerStyle.gradient.stops].sort((a: GradientStop, b: GradientStop) => a.value - b.value);
  
  const result: any = {
    stops: sortedStops,
    min: sortedStops[0]?.value ?? 0,
    max: sortedStops[sortedStops.length - 1]?.value ?? 0
  };
  
  if (layerStyle.gradient.defaultColor) {
    result.noData = { color: layerStyle.gradient.defaultColor };
  }
  
  return result;
}

// ============================================================================
// Фабрика статического стиля
// ============================================================================

export function createStyle(styleConfig: StyleConfig | null | undefined): Style {
  // Стиль по умолчанию — серая точка
  if (!styleConfig) {
    return new Style({
      image: new Circle({
        radius: 6,
        fill: new Fill({ color: '#95a5a6' }),
        stroke: new Stroke({ color: '#ffffff', width: 1 })
      })
    });
  }

  switch (styleConfig.type) {
    case 'circle': {
      const radius = styleConfig.radius || 6;
      let fill = createFill(styleConfig.fill);
      const stroke = createStroke(styleConfig.stroke || '#ffffff', styleConfig.strokeWidth || 1);


      // Если нет заливки — добавляем hit detection с невидимой заливкой
      // чтобы клик срабатывал по всей площади круга, а не только по контуру
      if(!fill) { fill = new Fill({ color: 'rgba(0,0,0,0.01)' }); } 
      const circleOptions: any = { radius, fill, stroke };

      return new Style({ image: new Circle(circleOptions) });
    }

    case 'line':
      return new Style({
        stroke: createStroke(styleConfig.stroke || '#e74c3c', styleConfig.strokeWidth || 3, styleConfig.lineDash)
      });

    case 'polygon':
      return new Style({
        fill: createFill(styleConfig.fill),
        stroke: createStroke(styleConfig.stroke || '#2980b9', styleConfig.strokeWidth || 2, styleConfig.lineDash)
      });

    case 'icon':
      return new Style({
        image: new Icon({
          src: styleConfig.src,
          scale: styleConfig.scale || 1,
          anchor: styleConfig.anchor || [0.5, 1],
          rotation: styleConfig.rotation || 0
        })
      });

    case 'square': {
      const size = styleConfig.size || 8;
      let fill = createFill(styleConfig.fill);
      const stroke = createStroke(styleConfig.stroke || '#ffffff', styleConfig.strokeWidth || 1);

      if(!fill) { fill = new Fill({ color: 'rgba(0,0,0,0.01)' }); } 
  
      const squareOptions: any = {
        points: 4,
        angle: Math.PI / 4,
        radius: size,
        fill,
        stroke
      };
  
      return new Style({ image: new RegularShape(squareOptions) });
    }

    case 'triangle': {
      const radius = styleConfig.radius || 8;
      let fill = createFill(styleConfig.fill);
      const stroke = createStroke(styleConfig.stroke || '#ffffff', styleConfig.strokeWidth || 1);

      if(!fill) { fill = new Fill({ color: 'rgba(0,0,0,0.01)' }); } 
  
      const triangleOptions: any = {
        points: 3,
        radius,
        rotation: styleConfig.rotation || 0,
        fill,
        stroke
      };
  
      return new Style({ image: new RegularShape(triangleOptions) });
    }

    case 'text':
      return new Style({
        text: new Text({
          text: styleConfig.text || '',
          font: styleConfig.font || `${styleConfig.size || 12}px sans-serif`,
          fill: createFill(styleConfig.fill),
          stroke: createStroke(styleConfig.stroke, styleConfig.strokeWidth || 3),
          offsetX: styleConfig.offsetX || 0,
          offsetY: styleConfig.offsetY || 0
        })
      });

    default:
      console.warn(`⚠️ Неизвестный тип стиля: ${(styleConfig as any).type}`);
      return new Style();
  }
}

// ============================================================================
// Фабрика динамической функции стиля (для слоёв с разными геометрияими)
// ============================================================================

/**
 * Создаёт функцию стиля, которая выбирает оформление на основе типа геометрии фичи.
 * Полезно для WFS-слоёв, где могут быть и точки, и линии, и полигоны.
 * 
 * @param styleConfig - базовый стиль (используется как fallback)
 * @param overrides - опциональные переопределения для разных типов геометрий
 */
export function createStyleFunction(
  styleConfig: StyleConfig | null | undefined,
  overrides?: {
    point?: StyleConfig;
    line?: StyleConfig;
    polygon?: StyleConfig;
  }
): (feature: FeatureLike, resolution: number) => Style { // <-- ИЗМЕНЕНА сигнатура
  const baseStyle = createStyle(styleConfig);
  const pointStyle = overrides?.point ? createStyle(overrides.point) : null;
  const lineStyle = overrides?.line ? createStyle(overrides.line) : null;
  const polygonStyle = overrides?.polygon ? createStyle(overrides.polygon) : null;

  return (feature: FeatureLike, _resolution: number): Style => { // <-- ИЗМЕНЕНА сигнатура
    const geom = feature.getGeometry();
    if (!geom) return baseStyle;

    const geomType = geom.getType();

    if (geomType === 'Point' || geomType === 'MultiPoint') {
      return pointStyle || baseStyle;
    }
    if (geomType === 'LineString' || geomType === 'MultiLineString') {
      return lineStyle || baseStyle;
    }
    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
      return polygonStyle || baseStyle;
    }

    return baseStyle;
  };
}

// ============================================================================
// Фабрика стиля с поддержкой атрибутов фичи
// ============================================================================

/**
 * Создаёт функцию стиля, которая может использовать значения атрибутов фичи.
 * Например, цвет точки может зависеть от значения поля "category".
 * 
 * @param styleConfig - базовый стиль
 * @param attributeMapping - соответствие "значение атрибута" → "стиль"
 * @param attributeName - имя поля в атрибутах фичи
 */
export function createAttributeBasedStyle(
  styleConfig: StyleConfig | null | undefined,
  attributeMapping: Record<string, StyleConfig>,
  attributeName: string
): (feature: FeatureLike, resolution: number) => Style { // <-- ИЗМЕНЕНА сигнатура
  const defaultStyle = createStyle(styleConfig);
  const styleCache = new Map<string, Style>();

  for (const [key, config] of Object.entries(attributeMapping)) {
    styleCache.set(key, createStyle(config));
  }

  return (feature: FeatureLike, _resolution: number): Style => { // <-- ИЗМЕНЕНА сигнатура
    const attrValue = feature.get(attributeName);
    if (attrValue === undefined || attrValue === null) return defaultStyle;

    const key = String(attrValue);
    return styleCache.get(key) || defaultStyle;
  };
}

// ============================================================================
// Получение стиля для слоя по его ID
// ============================================================================

/**
 * Возвращает стиль для указанного слоя из конфига LAYER_STYLES.
 * Если стиль не задан — возвращает default. Если и его нет — null.
 */
export function getLayerStyle(layerId: string): StyleConfig | null {
  const styles = getConfigOptional('layer-styles');
  if (!styles) return null;
  
  return (styles[layerId] as StyleConfig) ?? (styles['default'] as StyleConfig) ?? null;
}

/**
 * Возвращает готовый OpenLayers Style (или функцию стиля) для слоя.
 * Используется в layers.ts при создании векторных слоёв.
 */
export function getLayerOlStyle(
  layerId: string
): Style | ((feature: FeatureLike, _resolution: number) => Style) {
  const styles = getConfigOptional('layer-styles');
  if (!styles) return createStyle(null);

  const layerStyleConfig = styles[layerId];
  
  if (!layerStyleConfig) {
    const defaultStyle = styles['default'] as StyleConfig;
    return createStyle(defaultStyle ?? null);
  }

  if ('overrides' in layerStyleConfig) {
    const { base, overrides } = layerStyleConfig as any;
    return createStyleFunction(base, overrides);
  }
  
  if ('attributeMapping' in layerStyleConfig) {
    const { base, attributeMapping, attributeName } = layerStyleConfig as any;
    return createAttributeBasedStyle(base, attributeMapping, attributeName);
  }
  
  // НОВОЕ: Градиентная раскраска
  if ('gradient' in layerStyleConfig && layerStyleConfig.gradient) {
    const { gradient, ...baseStyleConfig } = layerStyleConfig as StyleConfig & { gradient: GradientConfig };
    return createGradientStyleFunction(baseStyleConfig, gradient);
  }
  
  if ('choropleth' in layerStyleConfig && layerStyleConfig.choropleth) {
    const { choropleth, ...baseStyleConfig } = layerStyleConfig as StyleConfig & { choropleth: ChoroplethConfig };
    return createChoroplethStyleFunction(baseStyleConfig, choropleth);
  }

  return createStyle(layerStyleConfig as StyleConfig);
}

// ============================================================================
// Стили для специальных состояний
// ============================================================================

/** Стиль для выделенной фичи (при клике) */
export function createSelectedStyle(): Style {
  return new Style({
    image: new Circle({
      radius: 10,
      fill: new Fill({ color: '#f39c12' }),
      stroke: new Stroke({ color: '#ffffff', width: 2 })
    }),
    stroke: new Stroke({ color: '#f39c12', width: 4 }),
    fill: new Fill({ color: 'rgba(243, 156, 18, 0.3)' })
  });
}

/** Стиль для фичи при наведении */
export function createHoverStyle(): Style {
  return new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({ color: '#3498db' }),
      stroke: new Stroke({ color: '#ffffff', width: 2 })
    }),
    stroke: new Stroke({ color: '#3498db', width: 4 }),
    fill: new Fill({ color: 'rgba(52, 152, 219, 0.2)' })
  });
}